import MeasurementSystems from 'utils/personalizer/MeasurementSystems';
import TimeUtil from 'utils/date/TimeUtil';
import formatNumber from 'utils/NumberFormatter';
import NumberFormats from 'utils/personalizer/NumberFormats';
import Personalizer from 'utils/Personalizer';
import Conversion from 'utils/Conversion';

export const HYDRATION_UNIT_KEY = 'nutritionAndHydrationRecommendation.hydrationUnit';
export const NUTRITION_UNIT_KEY = 'nutritionAndHydrationRecommendation.nutritionUnit';

export const HYDRATION_UNITS = {
    OUNCE: 'ounce',
    MILLILITER: 'milliliter'
};

export const NUTRITION_UNITS = {
    GRAM: 'gram',
    CALORIE: 'calorie'
};

export const WEATHER_OPTIONS = {
    TODAY_FORECAST: 'today',
    EVENT_FORECAST: 'event',
    CUSTOM_TEMPERATURE: 'custom'
};

export const UNIT_TRANSLATION_KEYS = {
    [HYDRATION_UNITS.OUNCE]: 'units:oz_unit_abbr',
    [HYDRATION_UNITS.MILLILITER]: 'units:label_ml',
    [NUTRITION_UNITS.GRAM]: 'units:grams_unit',
    [NUTRITION_UNITS.CALORIE]: 'units:calories_unit'
};

const METRIC_TEMPERATURE_RANGE = {
    min: 10,
    max: 32
};

const STATUTE_TEMPERATURE_RANGE = {
    min: 50,
    max: 90
};

export const getDefaultHydrationUnit = (userSettings) => {
    const measurementSystem = userSettings?.userData?.measurementSystem;

    switch (measurementSystem) {
        case MeasurementSystems.METRIC:
            return HYDRATION_UNITS.MILLILITER;
        case MeasurementSystems.STATUTE_UK:
        case MeasurementSystems.STATUTE_US:
            return HYDRATION_UNITS.OUNCE;
        default:
            return HYDRATION_UNITS.OUNCE;
    }
};

export const getTemperatureRange = (measurementSystem) => {
    return measurementSystem === MeasurementSystems.METRIC ? METRIC_TEMPERATURE_RANGE : STATUTE_TEMPERATURE_RANGE;
};

const isHydrationRecommended = (data) => {
    return data?.metricHydrationTotalValueInML > 0 && data?.statuteHydrationTotalValueInOz > 0;
};

const isNutritionRecommended = (data) => {
    return (
        data?.metricNutritionTotalValueLowInGrams > 0 &&
        data?.metricNutritionTotalValueHighInGrams > 0 &&
        data?.statuteNutritionTotalValueLowInGrams > 0 &&
        data?.statuteNutritionTotalValueHighInGrams > 0
    );
};

const getFormattedIntervalFromSeconds = (seconds) => {
    return TimeUtil.formatDescriptiveDuration(seconds, false, false);
};

export const getHydrationDataBasedOnUnit = (data, hydrationUnit) => {
    if (!isHydrationRecommended(data)) return null;

    if (hydrationUnit === HYDRATION_UNITS.MILLILITER) {
        return {
            totalValue: data.metricHydrationTotalValueInML,
            intervalValue: data.metricHydrationIntervalValueInML,
            intervalInSeconds: getFormattedIntervalFromSeconds(data.metricIntervalInSeconds),
            unitKey: UNIT_TRANSLATION_KEYS[HYDRATION_UNITS.MILLILITER]
        };
    }

    return {
        totalValue: data.statuteHydrationTotalValueInOz,
        intervalValue: data.statuteHydrationIntervalValueInOz,
        intervalInSeconds: getFormattedIntervalFromSeconds(data.statuteIntervalInSeconds),
        unitKey: UNIT_TRANSLATION_KEYS[HYDRATION_UNITS.OUNCE]
    };
};

export const getNutritionDataBasedOnUnit = (data, nutritionUnit) => {
    if (!isNutritionRecommended(data)) return null;

    const formatNutritionValue = (value) => {
        return formatNumber(
            nutritionUnit === NUTRITION_UNITS.GRAM ? value : Personalizer.convertGramsOfCarbsToCalories(value),
            NumberFormats.NO_DECIMAL_NOSEP
        );
    };

    const formatNutritionDataEntries = (data) => ({
        ...data,
        totalValueLow: formatNutritionValue(data.totalValueLow),
        totalValueHigh: formatNutritionValue(data.totalValueHigh),
        intervalLowValue: formatNutritionValue(data.intervalLowValue),
        intervalHighValue: formatNutritionValue(data.intervalHighValue),
        intervalInSeconds: getFormattedIntervalFromSeconds(data.intervalInSeconds)
    });

    if (nutritionUnit === NUTRITION_UNITS.GRAM) {
        return formatNutritionDataEntries({
            totalValueLow: data.metricNutritionTotalValueLowInGrams,
            totalValueHigh: data.metricNutritionTotalValueHighInGrams,
            intervalLowValue: data.metricNutritionIntervalValueLowInGrams,
            intervalHighValue: data.metricNutritionIntervalValueHighInGrams,
            intervalInSeconds: data.metricIntervalInSeconds,
            unitKey: UNIT_TRANSLATION_KEYS[NUTRITION_UNITS.GRAM]
        });
    }

    return formatNutritionDataEntries({
        totalValueLow: data.statuteNutritionTotalValueLowInGrams,
        totalValueHigh: data.statuteNutritionTotalValueHighInGrams,
        intervalLowValue: data.statuteNutritionIntervalValueLowInGrams,
        intervalHighValue: data.statuteNutritionIntervalValueHighInGrams,
        intervalInSeconds: data.statuteIntervalInSeconds,
        unitKey: UNIT_TRANSLATION_KEYS[NUTRITION_UNITS.CALORIE]
    });
};

export const getIntervalValuesWithUnit = ({ intervalLowValue, intervalHighValue, unitKey }, t) => {
    return `${intervalLowValue}-${intervalHighValue} ${t(unitKey)}`;
};

export const getPersonalizedTemperature = (temperature) => {
    return temperature ? Math.round(Personalizer.convertTemperature(temperature)) : '';
};

export const getTemperatureInCelsius = (temperature, measurementSystem) => {
    if (!temperature) return '';

    return measurementSystem === MeasurementSystems.METRIC
        ? temperature
        : Math.round(Conversion.convertFahrenheitToCelsius(temperature));
};
